import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOSchema from '../../components/feature/SEOSchema';
import { CREDIT_PACKS, CREDIT_COSTS, supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';

const SUPABASE_FUNCTIONS_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL + '/functions/v1';

const PricingPage = () => {
  const [selected, setSelected] = useState<string>('pack_1000');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://workflowtools.com';

  const schema = useMemo(() => [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Buy Credits — WorkflowTools',
      url: `${siteUrl}/pricing`,
      description: 'Purchase credit packs for WorkflowTools. Use credits to download professional templates, export PDFs, DOCX files, and more. Credits never expire.',
      isPartOf: { '@type': 'WebSite', url: siteUrl, name: 'WorkflowTools' },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Do credits expire?',
          acceptedAnswer: { '@type': 'Answer', text: 'No, your credits never expire. Use them whenever you need.' },
        },
        {
          '@type': 'Question',
          name: 'Can I use credits on any template?',
          acceptedAnswer: { '@type': 'Answer', text: 'Yes! Credits work across all templates, PDF exports, DOCX exports, and more.' },
        },
        {
          '@type': 'Question',
          name: 'How do I get my 100 free credits?',
          acceptedAnswer: { '@type': 'Answer', text: 'Create a free account and 100 credits are automatically added — no credit card needed.' },
        },
        {
          '@type': 'Question',
          name: 'What happens if I run out of credits?',
          acceptedAnswer: { '@type': 'Answer', text: 'Simply buy a new credit pack. You can purchase as many as you need at any time.' },
        },
        {
          '@type': 'Question',
          name: 'Is my payment secure?',
          acceptedAnswer: { '@type': 'Answer', text: 'Yes, all payments are processed securely through Stripe. We never store your card details.' },
        },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'WorkflowTools Credit Packs',
      description: 'Choose a credit pack. Credits never expire and work on all templates.',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '500 Credits', description: '500 credits for $4.99. Use for template downloads, PDF exports, and more.' },
        { '@type': 'ListItem', position: 2, name: '1000 Credits — Most Popular', description: '1000 credits for $8.99.' },
        { '@type': 'ListItem', position: 3, name: '2500 Credits — Best Value', description: '2500 credits for $19.99.' },
        { '@type': 'ListItem', position: 4, name: '5000 Credits', description: '5000 credits for $34.99.' },
      ],
    },
  ], [siteUrl]);

  const handleBuy = async (packId: string) => {
    if (!user) {
      navigate('/signup');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { navigate('/login'); return; }

      const response = await fetch(`${SUPABASE_FUNCTIONS_URL}/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          pack_id: packId,
          success_url: `${window.location.origin}/payment-success?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${window.location.origin}/pricing`,
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.url) throw new Error(data.error || 'Failed to start checkout');
      window.location.href = data.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOSchema schema={schema} id="pricing-schema" />
      <Navbar />

      <div className="pt-28 pb-20 px-6">
        <div className="max-w-4xl mx-auto">

          {/* Header */}
          <div className="text-center mb-14">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Buy Credits</h1>
            <p className="text-gray-500 text-lg max-w-xl mx-auto">
              Choose the credit pack that works for you. Credits never expire and can be used on any template.
            </p>
            <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-100 rounded-full px-4 py-2 mt-4">
              <i className="ri-coin-line text-amber-600 text-sm"></i>
              <span className="text-amber-700 text-sm font-medium">New users get 100 free credits on signup</span>
            </div>
          </div>

          {/* Credit Packs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-14">
            {CREDIT_PACKS.map((pack) => (
              <div
                key={pack.id}
                onClick={() => setSelected(pack.id)}
                className={`relative rounded-2xl p-7 border-2 cursor-pointer transition-all ${
                  selected === pack.id
                    ? 'border-gray-900 bg-gray-50'
                    : 'border-gray-100 bg-white hover:border-gray-200'
                }`}
              >
                {pack.popular && (
                  <div className="absolute -top-3.5 left-6 bg-gray-900 text-white text-xs font-bold px-3 py-1 rounded-full">
                    Most Popular
                  </div>
                )}
                {pack.bestValue && (
                  <div className="absolute -top-3.5 left-6 bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                    Best Value
                  </div>
                )}

                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center">
                        <i className="ri-coin-line text-amber-500"></i>
                      </div>
                      <span className="text-2xl font-bold text-gray-900">{pack.label}</span>
                    </div>
                    <p className="text-gray-400 text-sm">
                      ${(pack.price / pack.credits * 100).toFixed(1)}¢ per credit
                    </p>
                  </div>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1 ${
                    selected === pack.id ? 'border-gray-900 bg-gray-900' : 'border-gray-300'
                  }`}>
                    {selected === pack.id && <i className="ri-check-line text-white text-xs"></i>}
                  </div>
                </div>

                <div className="flex items-end justify-between mb-5">
                  <div>
                    <span className="text-4xl font-bold text-gray-900">${pack.price}</span>
                    <span className="text-gray-400 text-sm ml-1">one-time</span>
                  </div>
                  <div className="text-sm text-gray-500">
                    ~{Math.floor(pack.credits / 25)} PDF exports
                  </div>
                </div>

                <button
                  onClick={(e) => { e.stopPropagation(); handleBuy(pack.id); }}
                  disabled={loading}
                  className="w-full bg-gray-900 text-white font-semibold py-2.5 rounded-xl hover:bg-gray-800 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed text-sm whitespace-nowrap"
                >
                  {loading && selected === pack.id ? 'Redirecting...' : `Buy for $${pack.price}`}
                </button>
              </div>
            ))}
          </div>

          {error && (
            <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600 mb-6 max-w-md mx-auto">
              <i className="ri-error-warning-line flex-shrink-0"></i>
              <span>{error}</span>
            </div>
          )}

          {/* Credit Cost Table */}
          <div className="mt-16">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Credit cost per action</h2>
              <p className="text-gray-500 text-sm">Know exactly how much each action costs before you use it</p>
            </div>
            <div className="bg-gray-50 rounded-2xl overflow-hidden border border-gray-100">
              {[
                { action: 'PDF Export', credits: CREDIT_COSTS.PDF_EXPORT, icon: 'ri-file-pdf-line' },
                { action: 'DOCX Export', credits: CREDIT_COSTS.DOCX_EXPORT, icon: 'ri-file-word-line' },
                { action: 'Template Download', credits: CREDIT_COSTS.TEMPLATE_DOWNLOAD, icon: 'ri-download-2-line' },
                { action: 'Premium Template', credits: CREDIT_COSTS.PREMIUM_TEMPLATE, icon: 'ri-star-line' },
                { action: 'Bundle Download', credits: CREDIT_COSTS.BUNDLE_DOWNLOAD, icon: 'ri-archive-line' },
                { action: 'Save Draft', credits: CREDIT_COSTS.SAVE_DRAFT, icon: 'ri-save-line' },
              ].map((item, idx, arr) => (
                <div
                  key={idx}
                  className={`flex items-center justify-between px-6 py-4 ${
                    idx < arr.length - 1 ? 'border-b border-gray-100' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-white rounded-lg border border-gray-100 flex items-center justify-center">
                      <i className={`${item.icon} text-gray-600 text-sm`}></i>
                    </div>
                    <span className="text-gray-700 font-medium text-sm">{item.action}</span>
                  </div>
                  <div className="flex items-center gap-1.5 bg-amber-50 rounded-full px-3 py-1">
                    <i className="ri-coin-line text-amber-500 text-xs"></i>
                    <span className="text-amber-700 text-sm font-semibold">{item.credits} credits</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ */}
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Frequently asked questions</h2>
            <div className="space-y-4">
              {[
                { q: 'Do credits expire?', a: 'No, your credits never expire. Use them whenever you need.' },
                { q: 'Can I use credits on any template?', a: 'Yes! Credits work across all templates, PDF exports, DOCX exports, and more.' },
                { q: 'How do I get my 100 free credits?', a: 'Create a free account and 100 credits are automatically added — no credit card needed.' },
                { q: 'What happens if I run out of credits?', a: 'Simply buy a new credit pack. You can purchase as many as you need at any time.' },
                { q: 'Is my payment secure?', a: 'Yes, all payments are processed securely through Stripe. We never store your card details.' },
              ].map((faq, idx) => (
                <div key={idx} className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                  <h3 className="font-semibold text-gray-900 text-sm mb-2">{faq.q}</h3>
                  <p className="text-gray-500 text-sm">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PricingPage;
